import './Amount.scss';
import React from 'react';
import { formValueSelector, reduxForm } from 'redux-form';
import { newThemeInput, rangeInput } from '_fields/inputs';
import { parseCurrency } from '_fields/parsers';
import { Field } from 'redux-form';
import TermSelector from '@components/_shared/TermSelector';
import { useForm } from 'react-hook-form';
import { Form } from '@components/shadcn/components/ui/form';
import { Helmet } from 'react-helmet';
import { Loader, Text } from 'shared-components';
import { connect } from 'react-redux';
import { ApplyWizardSteps, STEP_AMOUNT } from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { AVAILABLE_TERM_OPTIONS } from '@store/actions';
import {
  LoanWizardShell,
  LoanSubmitButton,
  getLoanPhaseIndex,
  getLoanFaqForRoute,
} from '@components/Apply/WizardShell';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';

let Amount = (props) => {
  const rhfForm = useForm();
  const { currentPath, saving: contextSaving } = useApplyWizardContext();
  const phase = getLoanPhaseIndex(currentPath);
  const faq = getLoanFaqForRoute(currentPath);
  const isSubmitting = !!(props.saving || contextSaving);

  // prevent errors while waiting for config to load
  if (props.config.customVendor && !props.config.loaded) {
    return (
      <LoanWizardShell currentStepIndex={phase} faq={faq}>
        <div className="flex items-center justify-center w-full py-[60px]">
          <Loader />
        </div>
      </LoanWizardShell>
    );
  }

  const { min, max, rate } =
    props.config[
      props.loanType?.toUpperCase() === 'WC' ? 'WC' : props.loanType?.toUpperCase() === 'EQ' ? 'EQ' : 'UNKNOWN'
    ];

  return (
    <LoanWizardShell currentStepIndex={phase} faq={faq}>
      <Helmet>
        <title>Select Amount - Your Loan Application</title>
      </Helmet>

      <div className="flex items-center justify-center py-[20px] w-full">
        <Text variant="headline-sm" className="!font-extended !uppercase !text-[16px] !leading-none text-center">
          Financing Amount
        </Text>
      </div>

      <div className="flex flex-col gap-[10px] items-center w-full">
        <div className="flex items-center justify-center py-[10px] w-full">
          <Text variant="body-general" className="!text-[20px] !leading-[30px] text-center">
            Select Financing Amount
          </Text>
        </div>

        {props.showBanner && (
          <p className="noticeBanner">
            We noticed you are accessing this from outside the US. Please note we do not currently provide services to
            non US Applicants
          </p>
        )}

        <Form {...rhfForm}>
          <form onSubmit={props.handleSubmit} className="w-full">
            <div className="w-full flex flex-col gap-[10px]">
              {/* Slider on top, amount field below per Apply QA spec.
                  The "apply-again" variant draws min/max chips + a value
                  bubble below the track, so the wrapper can't have a
                  fixed height — let it use its natural ~90px and add
                  pb-[10px] so the bubble doesn't crowd the next label. */}
              <div className="w-full flex justify-center pb-[10px]">
                <div className="max-w-[368px] w-full" data-testid="loan_amount_slider">
                  <Field name="loan_amount" component={rangeInput} variant="apply-again" min={min} max={max} />
                </div>
              </div>

              <div className="w-full max-w-[368px] mx-auto">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  AMOUNT (USD)*
                </span>
                <Field
                  name="loan_amount"
                  label="Amount (USD)"
                  component={newThemeInput}
                  className="amount !bg-dawn3"
                  placeholder="Enter Amount"
                  formatAsNumber
                  parse={parseCurrency}
                  data-testid="loan-amount"
                />
              </div>

              <div className="w-full max-w-[368px] mx-auto pt-[10px]">
                <span className="font-condensed text-[14px] text-charcoal leading-[1.2] block text-left pb-[6px] w-full">
                  LOAN TERM*
                </span>
                <TermSelector
                  // TODO: replace AVAILABLE_TERM_OPTIONS with termOptions but it only supports WC and EQ right now.
                  termOptions={AVAILABLE_TERM_OPTIONS /* termOptions */}
                  loanAmount={props.loanAmount}
                  loanType={props.loanType}
                  rate={rate}
                  updatePayment={props.updatePayment}
                  terms={props.loan_terms}
                  config={props.config}
                  withMonthlyPayment
                />
              </div>

              <div className="flex flex-col items-center justify-center w-full pt-[20px] pb-[10px] px-[10px] gap-[10px]">
                <LoanSubmitButton invalid={props.invalid} saving={isSubmitting} data-testid="amount_continue">
                  Continue
                </LoanSubmitButton>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </LoanWizardShell>
  );
};

Amount = reduxForm({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_AMOUNT].zodSchema),
})(Amount);

const selector = formValueSelector('application');

const mapStateToProps = (state) => {
  return {
    loan_terms: selector(state, 'loan_terms'),
    loanPayment: selector(state, 'loan_payment'),
    config: state.config,
  };
};

Amount = connect(mapStateToProps)(Amount);

export default Amount;
